import { MapMouseEvent } from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";
import ReactMapGl, { Source, useMap } from "react-map-gl";
import { Route, Routes } from "react-router-dom";
import { useAuthStore, useContextMenuStore, useMapStore, useViewSettingsStore } from "..";
import useCtrlDragRotate from "../hooks/useCtrlDragRotate";
import useIsHalfScreenMap from "../hooks/useIsHalfScreenMap";
import useOpenPropertyOnDoubleClick from "../hooks/useOpenPropertyOnDoubleClick";
import BaseMap from "../interfaces/BaseMap";
import BuildingsBaseMapLayer from "./baseMapLayers/BuildingsBaseMapLayer";
import HybridMapboxBaseMapLayer from "./baseMapLayers/HybridMapboxBaseMapLayer";
import SatelliteArcGISBaseMapLayer from "./baseMapLayers/SatelliteArcGISBaseMapLayer";
import SatelliteBingBaseMapLayer from "./baseMapLayers/SatelliteBingBaseMapLayer";
import SatelliteGoogleBaseMapLayer from "./baseMapLayers/SatelliteGoogleBaseMapLayer";
import SatelliteMapboxBaseMapLayer from "./baseMapLayers/SatelliteMapboxBaseMapLayer";
import SatelliteNAIPBaseMapLayer from "./baseMapLayers/SatelliteNAIPBaseMapLayer";
import DealsMapLayer, { lostDealPoligonLayerId, openDealPoligonLayerId, wonDealPoligonLayerId } from "./deals/DealsMapLayer";
import LeadCreatePopup from "./leads/LeadCreatePopup";
import LeadsMapLayer, { leadMarkerLayerId } from "./leads/LeadsMapLayer";
import LeaseCompsMapLayer, { leaseCompMarkerLayerId } from "./leaseComps/LeaseCompsMapLayer";
import LocationsMapLayer, { locationsLayerId } from "./locations/LocationsMapLayer";
import styles from "./Map.module.css";
import { defaultMapStyle } from "./mapConfig";
import BaseMapMapControl from "./mapControls/BaseMapMapControl";
import DrawMapControl, { readonlyDrawMapLayerId } from "./mapControls/DrawMapControl";
import EmptyMapControl from "./mapControls/EmptyMapControl";
import LayersMapControl from "./mapControls/LayersMapControl";
import ZoomMapControl from "./mapControls/ZoomMapControl";
import AADTMapLayer from "./mapLayers/AADTMapLayer";
import CountiesMapLayer from "./mapLayers/CountiesMapLayer";
import DriveTimeMapLayer from "./mapLayers/DriveTimeMapLayer";
import IosZoningMapLayer from "./mapLayers/IosZoningMapLayer";
import OpportunityZonesMapLayer from "./mapLayers/OpportunityZonesMapLayer";
import ParcelsMapLayer from "./mapLayers/ParcelsMapLayer";
import PointsOfInterestMapLayer from "./mapLayers/PointsOfInterestMapLayer";
import RadiusMapLayer from "./mapLayers/RadiusMapLayer";
import RailsMapLayer from "./mapLayers/RailsMapLayer";
import RecordedSalesMapLayer, { recordedSalesLayerId } from "./mapLayers/RecordedSalesMapLayer";
import RegridZoningMapLayer from "./mapLayers/RegridZoningMapLayer";
import StateShapesMapLayer from "./mapLayers/StateShapesMapLayer";
import ZoningMapLayer from "./mapLayers/ZoningMapLayer";
import MapMarker from "./MapMarker";
import MapShapesLayer from "./MapShapesLayer";
import PopupController from "./PopupController";
import FavoritePropertiesMaplayer, { favoritePropertyPoligonLayerId } from "./property/FavoritePropertiesMaplayer";
import SaleCompsMapLayer, { saleCompMarkerLayerId } from "./saleComps/SaleCompsMapLayer";

function Map() {
    const { map } = useMap();
    const mapContainer = useRef(null);
    const isHalfScreenMap = useIsHalfScreenMap();
    const mapStore = useMapStore();
    const authStore = useAuthStore();
    const contextMenuStore = useContextMenuStore();
    const viewSettingsStore = useViewSettingsStore();
    const [cursor, setCursor] = useState<string>();
    useOpenPropertyOnDoubleClick();
    useCtrlDragRotate();

    useEffect(() => {
        if (map) {
            map.on("load", () => mapStore.setIsStyleLoaded());
        }
    }, [map]);

    useEffect(() => {
        if (map && mapContainer.current) {
            const mapObserver = new ResizeObserver(() => {
                map.stop();
                map.resize();
            });

            mapObserver.observe(mapContainer.current);

            return () => mapObserver.disconnect();
        }
    }, [map, mapContainer.current]);

    useEffect(() => {
        const unsubscribeMouseMove = mapStore.onMouseMove("map", () => setCursor("pointer"));
        const unsubscribeMouseLeave = mapStore.onMouseLeave("map", () => setCursor(undefined));

        return () => {
            unsubscribeMouseMove();
            unsubscribeMouseLeave();
        }
    }, []);

    const onMapMouseEvent = (e: MapMouseEvent) => mapStore.callMapMouseEventCallbacks(e);

    return (
        <div className={isHalfScreenMap ? styles.halfScreenContainer : styles.container} ref={mapContainer}>
            <ReactMapGl
                id="map"
                {...mapStore.viewState}
                onMove={e => mapStore.setViewState(e.viewState)}
                mapboxAccessToken={authStore.publicMapboxAccessToken!}
                mapStyle={authStore.userSettings!.mapboxStyleUrl || defaultMapStyle}
                dragRotate={false}
                cursor={cursor}
                onMouseMove={onMapMouseEvent}
                onMouseLeave={onMapMouseEvent}
                onMouseDown={onMapMouseEvent}
                onMouseUp={onMapMouseEvent}
                onClick={onMapMouseEvent}
                onDblClick={onMapMouseEvent}
                interactiveLayerIds={[wonDealPoligonLayerId, openDealPoligonLayerId, lostDealPoligonLayerId, leadMarkerLayerId, leaseCompMarkerLayerId, saleCompMarkerLayerId, recordedSalesLayerId, locationsLayerId, favoritePropertyPoligonLayerId, readonlyDrawMapLayerId]}
                onContextMenu={e => { if (!e.originalEvent.ctrlKey) contextMenuStore.open(e.lngLat, { x: e.originalEvent.x, y: e.originalEvent.y }); }}
                boxZoom={mapStore.isStyleLoaded} // boxZoom works only if pass false first and then true or undefined
                terrain={{ source: 'mapbox-dem', exaggeration: 1 }}
            >
                <Source id="mapbox-dem" type="raster-dem" url="mapbox://mapbox.mapbox-terrain-dem-v1" />
                <EmptyMapControl height={60} />
                {viewSettingsStore.isMobile && <BaseMapMapControl />}
                <LayersMapControl />
                <ZoomMapControl />

                <SatelliteMapboxBaseMapLayer visible={mapStore.baseMap === BaseMap.SatelliteMapbox} />
                <SatelliteGoogleBaseMapLayer visible={mapStore.baseMap === BaseMap.SatelliteGoogle} />
                <SatelliteArcGISBaseMapLayer visible={mapStore.baseMap === BaseMap.SatelliteArcGIS} />
                <SatelliteBingBaseMapLayer visible={mapStore.baseMap === BaseMap.SatelliteBing} />
                <SatelliteNAIPBaseMapLayer visible={mapStore.baseMap === BaseMap.SatelliteNAIP} />
                <HybridMapboxBaseMapLayer visible={mapStore.baseMap === BaseMap.HybridMapbox} />
                <BuildingsBaseMapLayer visible={mapStore.baseMap === BaseMap.Streets} />

                <ParcelsMapLayer visible={mapStore.visibleLayers.parcels} />
                <ZoningMapLayer />
                <RegridZoningMapLayer zoningVisible={mapStore.visibleLayers.zoning} landUseVisible={mapStore.visibleLayers.landUse} iosZoningVisible={mapStore.visibleLayers.iosZoning} />
                <IosZoningMapLayer visible={mapStore.visibleLayers.iosZoning} />
                <CountiesMapLayer visible={mapStore.visibleLayers.counties} />
                <AADTMapLayer visible={mapStore.visibleLayers.aadt} />
                <OpportunityZonesMapLayer visible={mapStore.visibleLayers.opportunityZones} />
                <RailsMapLayer visible={mapStore.visibleLayers.rails} />
                <PointsOfInterestMapLayer visible={mapStore.visibleLayers.pointsOfInterest} />
                <StateShapesMapLayer />

                <DealsMapLayer
                    wonVisible={mapStore.visibleLayers.wonDeals}
                    lostVisible={mapStore.visibleLayers.lostDeals}
                    openVisible={mapStore.visibleLayers.openDeals}
                />
                <FavoritePropertiesMaplayer visible={mapStore.visibleLayers.favorites} />
                <LeaseCompsMapLayer visible={mapStore.visibleLayers.leaseComps} />
                <SaleCompsMapLayer visible={mapStore.visibleLayers.saleComps} />
                <LeadsMapLayer visible={mapStore.visibleLayers.leads} />

                {!viewSettingsStore.isMobile && <DrawMapControl />}
                {!viewSettingsStore.isMobile && <MapMarker />}
                {!viewSettingsStore.isMobile && <DriveTimeMapLayer />}
                <RadiusMapLayer />
                <LocationsMapLayer />
                <RecordedSalesMapLayer />
                <MapShapesLayer />
                {!viewSettingsStore.isMobile && <PopupController />}

                {!viewSettingsStore.isMobile && (
                    <Routes>
                        <Route path="leads/create/:longitude/:latitude" element={<LeadCreatePopup />} />
                    </Routes>
                )}
            </ReactMapGl>
        </div>
    );
}

export default observer(Map);
